export const MEDIA_CATALOG_ORIGIN = 'https://media.chio-chio.ru';
const CACHE_TTL = 30 * 60 * 1000;
let cachedCatalog;
let expiresAt = 0;
let pending;
let failedAt = 0;

export function designCatalogLink(article) {
    return `${MEDIA_CATALOG_ORIGIN}/?q=${encodeURIComponent(article)}#catalog`;
}

// Read visible text only: HTML attributes, scripts and other hidden markup
// must not attach an unrelated layout to a historical CRM message.
export function extractDesignArticles(body) {
    let text = String(body ?? '');
    if (typeof DOMParser !== 'undefined') {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        doc.querySelectorAll('script,style,template,noscript,iframe,object,embed,svg,math,canvas,form,textarea,button,select').forEach(node => node.remove());
        doc.querySelectorAll('br,p,div,li,h1,h2,h3,h4,h5,h6,blockquote,pre').forEach(node => node.append(' '));
        text = doc.body.textContent || '';
    }
    const articles = new Set();
    const pattern = /(?:^|[^0-9a-zа-яё_])[mм]\s*(\d{4,})(?![0-9a-zа-яё_])/gi;
    let match;
    while ((match = pattern.exec(text)) && articles.size < 20) articles.add(`M${match[1]}`);
    return Array.from(articles);
}

function catalogUrl(value, thumbnail = false) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
        const url = new URL(value, MEDIA_CATALOG_ORIGIN);
        const allowedPath = thumbnail
            ? /^\/media-catalog\/data\/thumbs\/[^/]+\.webp$/.test(url.pathname)
            : url.pathname.startsWith('/index.php/s/');
        return url.origin === MEDIA_CATALOG_ORIGIN && !url.username && !url.password && allowedPath ? url.href : null;
    } catch {
        return null;
    }
}

export function indexDesignCatalog(data) {
    if (!Array.isArray(data?.categories) || !data.categories.length) throw new Error('Invalid design catalog');
    const layouts = new Map();
    for (const category of data.categories) {
        for (const card of category.cards || []) {
            for (const layout of card.layouts || []) {
                if (!/^M\d{4,}$/.test(layout.article || '')) continue;
                if (layouts.has(layout.article)) throw new Error('Duplicate design article');
                layouts.set(layout.article, {
                    article: layout.article,
                    title: String(layout.title || layout.article),
                    category: String(category.name || ''),
                    thumbnail: catalogUrl(layout.thumbnail, true),
                    folderUrl: catalogUrl(layout.url),
                    files: (layout.files || []).map(file => ({
                        name: String(file.name || 'Файл'),
                        format: String(file.format || 'Файл'),
                        url: catalogUrl(file.downloadUrl),
                    })).filter(file => file.url),
                });
            }
        }
    }
    if (!layouts.size) throw new Error('Empty design catalog');
    return layouts;
}

export function loadDesignCatalog({ retry = false } = {}) {
    if (cachedCatalog && expiresAt > Date.now()) return Promise.resolve(cachedCatalog);
    if (pending) return pending;
    if (!retry && failedAt && Date.now() - failedAt < 30000) return Promise.reject(new Error('Catalog unavailable'));
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    // This is public metadata. Never use the authenticated Support API client
    // or send cookies, JWTs, CSRF headers or ticket text to the media host.
    pending = fetch(`${MEDIA_CATALOG_ORIGIN}/media-catalog/data/catalog.json`, {
        credentials: 'omit', mode: 'cors', referrerPolicy: 'no-referrer', signal: controller.signal,
    }).then(response => {
        if (!response.ok) throw new Error('Catalog unavailable');
        return response.json();
    }).then(data => {
        cachedCatalog = indexDesignCatalog(data);
        expiresAt = Date.now() + CACHE_TTL;
        failedAt = 0;
        return cachedCatalog;
    }).catch(error => {
        failedAt = Date.now();
        throw error;
    }).finally(() => {
        clearTimeout(timer);
        pending = undefined;
    });
    return pending;
}
